import is from '@sindresorhus/is';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import type { JobArchitectureSearchItem } from '~/shared/data-contract/job-architecture';
import type { JobArchitectureSearchResponseWithDetail } from '~/shared/data-contract/job-architecture-search';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { useJobArchitectureContentLanguage } from '../providers/JobArchitectureLanguageProvider';
import { jobArchitectureKeys } from './query-keys';

const PAGE_SIZE = 50;

export type RoleComparisonSearchResult = {
    readonly id: string;
    // Cluster for a role, parent role for a level; both let the picker label same-named siblings.
    readonly parentId: string | undefined;
    readonly title: string;
};

export type UseRoleComparisonSearchResult = {
    readonly error: Error | null;
    readonly fetchNextPage: () => void;
    readonly hasNextPage: boolean;
    readonly isFetchingNextPage: boolean;
    readonly isLoading: boolean;
    readonly results: readonly RoleComparisonSearchResult[];
};

const toSearchResult = (
    item: JobArchitectureSearchItem,
    itemType: 'job-level' | 'job-role',
): RoleComparisonSearchResult => ({
    id: item.id,
    parentId: itemType === 'job-role' ? item.path['cluster'] : item.path['role'],
    title: item.title,
});

// Searches active items of one kind across the whole architecture, so any cluster or role can contribute.
export const useRoleComparisonSearch = ({
    architectureId,
    enabled,
    excludedItemIds,
    itemType,
    searchTerm,
}: {
    readonly architectureId: string;
    readonly enabled: boolean;
    readonly excludedItemIds: readonly string[];
    readonly itemType: 'job-level' | 'job-role';
    readonly searchTerm: string;
}): UseRoleComparisonSearchResult => {
    const fetchers = useApplicationFetchers();
    const acceptLanguage = useJobArchitectureContentLanguage();
    const trimmedSearchTerm = searchTerm.trim();

    const query = useInfiniteQuery<
        JobArchitectureSearchResponseWithDetail,
        Error,
        InfiniteData<JobArchitectureSearchResponseWithDetail, number>,
        ReturnType<typeof jobArchitectureKeys.roleComparisonSearch>,
        number
    >({
        enabled,
        getNextPageParam: (lastPage) =>
            lastPage.pagination.page + 1 < lastPage.pagination.pages ? lastPage.pagination.page + 1 : undefined,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const result = await fetchers.searchJobArchitecture({
                acceptLanguage,
                jobArchitectureId: architectureId,
                page: pageParam,
                searchTerm: trimmedSearchTerm,
                size: PAGE_SIZE,
                sort: { direction: 'asc', field: 'title' },
                type: itemType === 'job-role' ? 'role' : 'level',
            });

            if (result.isErr) {
                throw result.error;
            }

            return result.value;
        },
        queryKey: jobArchitectureKeys.roleComparisonSearch(architectureId, itemType, trimmedSearchTerm, acceptLanguage),
        staleTime: 10_000,
        throwOnError: false,
    });

    const results = useMemo(
        () =>
            (query.data?.pages ?? [])
                .flatMap((page) => page.data)
                .filter((item) => item.state !== 'archived' && !excludedItemIds.includes(item.id))
                .map((item) => toSearchResult(item, itemType)),
        [excludedItemIds, itemType, query.data],
    );

    // Archived and already compared items are dropped locally, so a page can come back empty while
    // later pages still hold candidates. Keep paging until something is left to show.
    const isSkippingEmptyPages = is.null(query.error) && is.emptyArray(results) && query.hasNextPage;

    useEffect(() => {
        if (isSkippingEmptyPages && !query.isFetching) {
            void query.fetchNextPage();
        }
    }, [isSkippingEmptyPages, query.fetchNextPage, query.isFetching]);

    return {
        error: query.error,
        fetchNextPage: () => {
            void query.fetchNextPage();
        },
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isLoading: query.isLoading || isSkippingEmptyPages,
        results,
    };
};
